import { useState } from 'react';
import { ChevronDown, ChevronUp, AlertOctagon, AlertTriangle, AlertCircle, Info, Ban, CheckCircle2, Loader2 } from 'lucide-react';
import { SEVERITY_LABELS } from '../config/labels';
import type { TriggeredFlag } from '../api/review';
import type { FlagFeedback, FlagFeedbackDisposition } from '../api/feedback';
import { humanizeFactSummary, humanizeSourceCitation } from '../utils/flagText';

interface FlagCardProps {
  flag: TriggeredFlag;
  onOverride?: (flagId: string, reason: string) => Promise<void>;
  // Restore an overridden flag (unmark as false positive). Optional.
  onUnoverride?: (flagId: string) => Promise<void>;
  // learning-cycle feedback (assigned personnel only).
  canFeedback?: boolean;
  feedback?: FlagFeedback | null;
  onFeedback?: (
    flagId: string,
    disposition: FlagFeedbackDisposition,
    note?: string,
  ) => Promise<void>;
}

const SEVERITY_CONFIG: Record<string, { icon: typeof Info; badge: string; border: string }> = {
  AUTO_ESCALATE: { icon: AlertOctagon, badge: 'bg-red-100 text-red-700 border-red-300', border: 'border-l-red-500' },
  HIGH: { icon: AlertTriangle, badge: 'bg-orange-100 text-orange-700 border-orange-300', border: 'border-l-orange-500' },
  MEDIUM: { icon: AlertCircle, badge: 'bg-amber-100 text-amber-700 border-amber-300', border: 'border-l-amber-500' },
  LOW: { icon: Info, badge: 'bg-slate-100 text-slate-600 border-slate-300', border: 'border-l-slate-400' },
};

// Backend returns severity LOWERCASE; the config map is keyed UPPERCASE.
// Normalize the lookup key so badges render colored regardless of case.
function severityConfig(sev: string) {
  return SEVERITY_CONFIG[(sev ?? '').toUpperCase()] || SEVERITY_CONFIG.LOW;
}

// feedback disposition badge styles + labels.
const FEEDBACK_LABEL: Record<FlagFeedbackDisposition, string> = {
  confirmed: 'Confirmed',
  false_positive: 'Marked FP',
  needs_review: 'Needs review',
};
const FEEDBACK_BADGE: Record<FlagFeedbackDisposition, string> = {
  confirmed: 'bg-green-50 text-green-700 border-green-300',
  false_positive: 'bg-rose-50 text-rose-700 border-rose-300',
  needs_review: 'bg-amber-50 text-amber-700 border-amber-300',
};

export default function FlagCard({ flag, onOverride, onUnoverride, canFeedback, feedback, onFeedback }: FlagCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // feedback composer state.
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [feedbackDisposition, setFeedbackDisposition] = useState<FlagFeedbackDisposition | null>(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const config = severityConfig(flag.severity);
  const Icon = config.icon;
  const overridden = flag.override_status === 'overridden';
  // Humanize the [TAG] prefix + private(...) vs public(...) repr + linkify
  // URLs once for both the collapsed and expanded views.
  const humanized = humanizeFactSummary(flag.fact_summary);
  const citationHtml = humanizeSourceCitation(flag.source_citation || '');

  return (
    <div
      className={`card border-l-4 ${config.border} overflow-hidden transition-shadow hover:shadow-card-hover ${overridden ? 'opacity-60' : ''}`}
    >
      <button
        className="w-full flex items-start gap-3 p-4 text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <Icon size={20} className={`shrink-0 mt-0.5 ${overridden ? 'text-slate-300' : 'text-slate-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${config.badge}`}>
              {SEVERITY_LABELS[(flag.severity as string).toUpperCase()] || flag.severity}
            </span>
            <span className="text-xs text-slate-500">Cat {flag.category}</span>
            <span className={`text-xs font-semibold ${overridden ? 'line-through text-slate-400' : 'text-slate-600'}`}>{flag.points} pts</span>
            {flag.is_cross_check && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-200">
                Cross-check
              </span>
            )}
            {humanized.badgeLabel && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300 font-medium uppercase tracking-wide">
                {humanized.badgeLabel}
              </span>
            )}
            {flag.source_badge === 'learned' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-300 font-medium">
                Learned{flag.version_no != null ? ` v${flag.version_no}` : ''}{flag.rule_name ? ` - ${flag.rule_name}` : ''}
              </span>
            )}
            {flag.source_badge === 'hardcoded' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-medium">
                Hardcoded
              </span>
            )}
            {flag.source_badge === 'learned' && flag.confidence != null && (
              <span className="text-xs text-slate-500">confidence: {(flag.confidence * 100).toFixed(0)}%</span>
            )}
            {overridden && (
              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-300">
                <CheckCircle2 size={12} /> False positive
              </span>
            )}
            {feedback && (
              <span className={`text-xs px-1.5 py-0.5 rounded border ${FEEDBACK_BADGE[feedback.disposition]}`}>
                {FEEDBACK_LABEL[feedback.disposition]}
              </span>
            )}
          </div>
          <p className={`text-sm font-medium mb-1 ${overridden ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{flag.description}</p>
          <p
            className="text-sm text-slate-600"
            dangerouslySetInnerHTML={{ __html: humanized.bodyHtml }}
          />
          {flag.source_citation && (
            <p className="text-xs text-slate-500 mt-1">
              <span className="font-semibold">Source:</span>{' '}
              <span dangerouslySetInnerHTML={{ __html: citationHtml }} />
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronUp size={18} className="shrink-0 text-slate-400 mt-1" />
        ) : (
          <ChevronDown size={18} className="shrink-0 text-slate-400 mt-1" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-0 ml-8 border-t border-slate-100 mt-1 pt-3">
          {flag.source_citation && (
            <div className="mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Source</span>
              <p
                className="text-sm text-slate-600 mt-0.5"
                dangerouslySetInnerHTML={{ __html: citationHtml }}
              />
            </div>
          )}
          {/* The internal `flag_code` block was removed: flag_code is
              internal-only (not customer-facing) per the existing comment +
              operator request 2026-10-27. */}
          {/* Raw provenance JSON is hidden from the business user -- it is
              internal-only (status / category / flag_code) and not a
              customer-facing artifact. Operator request 2026-10-26. */}
          {/* (provenance block removed) */}

          {/* Override / mark as false positive */}
          {overridden ? (
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={14} className="text-slate-500" />
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Marked as false positive</span>
              </div>
              <p className="text-sm text-slate-600">{flag.override_reason || 'No reason provided.'}</p>
              {onUnoverride && (
                <button
                  type="button"
                  disabled={restoring}
                  onClick={async () => {
                    setRestoring(true);
                    try {
                      await onUnoverride(flag.id);
                    } finally {
                      setRestoring(false);
                    }
                  }}
                  className="mt-2 btn-secondary text-xs inline-flex items-center gap-1"
                  title="Restore this flag so it contributes to the score again"
                  data-testid="flag-unoverride"
                >
                  {restoring ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  Restore flag
                </button>
              )}
            </div>
          ) : onOverride ? (
            <div className="mt-3">
              {overriding ? (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <label className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                    Reason this flag is a false positive
                  </label>
                  <textarea
                    className="mt-1 w-full text-sm rounded border border-amber-300 p-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    rows={2}
                    placeholder="e.g. '(LOSS)' is the gain/loss column header, not the ticker"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      className="btn-primary text-sm inline-flex items-center gap-1"
                      disabled={submitting || !reason.trim()}
                      onClick={async () => {
                        setSubmitting(true);
                        try {
                          await onOverride(flag.id, reason.trim());
                          setOverriding(false);
                          setReason('');
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      {submitting ? 'Saving…' : 'Confirm override'}
                    </button>
                    <button className="btn-secondary text-sm" onClick={() => { setOverriding(false); setReason(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-secondary text-sm inline-flex items-center gap-1 text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => setOverriding(true)}
                >
                  <Ban size={14} /> Mark as false positive
                </button>
              )}
            </div>
          ) : null}

          {/*  learning-cycle feedback: confirm / false-positive /
              needs-review disposition (assigned personnel only). */}
          {canFeedback && onFeedback && (
            <div className="mt-3 p-3 rounded-lg bg-indigo-50/40 border border-indigo-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">
                  Feedback
                </span>
                {feedback && (
                  <span className={`text-xs px-1.5 py-0.5 rounded border ${FEEDBACK_BADGE[feedback.disposition]}`}>
                    {FEEDBACK_LABEL[feedback.disposition]}
                  </span>
                )}
              </div>
              {feedbackMode ? (
                <div>
                  <div className="flex gap-2 flex-wrap mb-2">
                    {(['confirmed', 'false_positive', 'needs_review'] as FlagFeedbackDisposition[]).map((d) => (
                      <button
                        key={d}
                        className={`text-xs px-2 py-1 rounded border ${feedbackDisposition === d ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                        onClick={() => setFeedbackDisposition(d)}
                      >
                        {FEEDBACK_LABEL[d]}
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="w-full text-sm rounded border border-indigo-200 p-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    rows={2}
                    placeholder="Optional note for the learning cycle…"
                    value={feedbackNote}
                    onChange={(e) => setFeedbackNote(e.target.value)}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      className="btn-primary text-sm inline-flex items-center gap-1"
                      disabled={submitting || feedbackDisposition == null}
                      onClick={async () => {
                        if (feedbackDisposition == null) return;
                        setSubmitting(true);
                        try {
                          await onFeedback(flag.id, feedbackDisposition, feedbackNote.trim() || undefined);
                          setFeedbackMode(false);
                          setFeedbackDisposition(null);
                          setFeedbackNote('');
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      {submitting ? 'Saving…' : 'Save feedback'}
                    </button>
                    <button
                      className="btn-secondary text-sm"
                      onClick={() => { setFeedbackMode(false); setFeedbackDisposition(null); setFeedbackNote(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-secondary text-sm inline-flex items-center gap-1 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                  onClick={() => setFeedbackMode(true)}
                >
                  {feedback ? 'Update feedback' : 'Add feedback'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}